import find from 'lodash/find'

export type AddressT = {
  city: string
  administrativeArea: string
  postalCode: string
  formattedAddressLines: Array<string>
  countryCode: string
}

// Matches 5 digit (short) zip-code, or extended 5+4 digit zip-code
const extendedZipCodeRegex = /^\d{5}(-\d{4})?$/

// Attempt to parse a generic administrative area as a US State
export function stateFromAdministrativeArea(administrativeArea: string): string | undefined | null {
  const normalized = administrativeArea.trim().toUpperCase()
  const state = find(usStates, state => state.abbreviation === normalized)
  return state === null || state === undefined ? null : state.abbreviation
}

// Attempt to parse a generic postal code as a 5-digit zip code
export function zipCodeFromPostalCode(postalCode: string): string | undefined | null {
  const trimmed = postalCode.trim()

  if (extendedZipCodeRegex.test(trimmed)) {
    // School search only uses 5-digit zips, so only grab the prefix
    return trimmed.substring(0, 5)
  }
  return null
}

type USStateT = {
  abbreviation: string
  name: string
}

export const usStates: Array<USStateT> = [
  {
    abbreviation: 'IT',
    name: 'International (prefer using a different country code)'
  },
  {abbreviation: 'AA', name: 'Armed Forces Americas (except Canada)'},
  {
    abbreviation: 'AE',
    name: 'Armed Forces (Africa, Canada, Europe, or Middle East)'
  },
  {abbreviation: 'AK', name: 'Alaska'},
  {abbreviation: 'AL', name: 'Alabama'},
  {abbreviation: 'AP', name: 'Armed Forces Pacific'},
  {abbreviation: 'AR', name: 'Arkansas'},
  {abbreviation: 'AS', name: 'American Samoa'},
  {abbreviation: 'AZ', name: 'Arizona'},
  {abbreviation: 'CA', name: 'California'},
  {abbreviation: 'CO', name: 'Colorado'},
  {abbreviation: 'CT', name: 'Connecticut'},
  {abbreviation: 'DC', name: 'District of Columbia'},
  {abbreviation: 'DE', name: 'Delaware'},
  {abbreviation: 'FL', name: 'Florida'},
  {abbreviation: 'GA', name: 'Georgia'},
  {abbreviation: 'GU', name: 'Guam'},
  {abbreviation: 'HI', name: 'Hawaii'},
  {abbreviation: 'IA', name: 'Iowa'},
  {abbreviation: 'ID', name: 'Idaho'},
  {abbreviation: 'IL', name: 'Illinois'},
  {abbreviation: 'IN', name: 'Indiana'},
  {abbreviation: 'KS', name: 'Kansas'},
  {abbreviation: 'KY', name: 'Kentucky'},
  {abbreviation: 'LA', name: 'Louisiana'},
  {abbreviation: 'MA', name: 'Massachusetts'},
  {abbreviation: 'MD', name: 'Maryland'},
  {abbreviation: 'ME', name: 'Maine'},
  {abbreviation: 'MI', name: 'Michigan'},
  {abbreviation: 'MN', name: 'Minnesota'},
  {abbreviation: 'MO', name: 'Missouri'},
  {abbreviation: 'MP', name: 'Northern Mariana Islands'},
  {abbreviation: 'MS', name: 'Mississippi'},
  {abbreviation: 'MT', name: 'Montana'},
  {abbreviation: 'NC', name: 'North Carolina'},
  {abbreviation: 'ND', name: 'North Dakota'},
  {abbreviation: 'NE', name: 'Nebraska'},
  {abbreviation: 'NH', name: 'New Hampshire'},
  {abbreviation: 'NJ', name: 'New Jersey'},
  {abbreviation: 'NM', name: 'New Mexico'},
  {abbreviation: 'NV', name: 'Nevada'},
  {abbreviation: 'NY', name: 'New York'},
  {abbreviation: 'OH', name: 'Ohio'},
  {abbreviation: 'OK', name: 'Oklahoma'},
  {abbreviation: 'OR', name: 'Oregon'},
  {abbreviation: 'PA', name: 'Pennsylvania'},
  {abbreviation: 'PR', name: 'Puerto Rico'},
  {abbreviation: 'RI', name: 'Rhode Island'},
  {abbreviation: 'SC', name: 'South Carolina'},
  {abbreviation: 'SD', name: 'South Dakota'},
  {abbreviation: 'TN', name: 'Tennessee'},
  {abbreviation: 'TX', name: 'Texas'},
  {abbreviation: 'UT', name: 'Utah'},
  {abbreviation: 'VA', name: 'Virginia'},
  {abbreviation: 'VI', name: 'Virgin Islands'},
  {abbreviation: 'VT', name: 'Vermont'},
  {abbreviation: 'WA', name: 'Washington'},
  {abbreviation: 'WI', name: 'Wisconsin'},
  {abbreviation: 'WV', name: 'West Virginia'},
  {abbreviation: 'WY', name: 'Wyoming'}
]

export type WeakAddressT = {
  city: string
  administrativeArea: string | undefined | null
  postalCode: string | undefined | null
  formattedAddressLines: Array<string> | undefined | null
  countryCode: string
}

export function extractAddress<A, E extends A & WeakAddressT>(entity: E): AddressT {
  const city = entity.city
  const administrativeArea = entity.administrativeArea || ''
  const postalCode = entity.postalCode || ''
  const formattedAddressLines = entity.formattedAddressLines || []
  const countryCode = entity.countryCode

  return {
    city,
    administrativeArea,
    postalCode,
    formattedAddressLines,
    countryCode
  }
}
